<script>
const MINUTE_IN_MILLI_SECONDS = 60000;
const HOUR_IN_MILLI_SECONDS = MINUTE_IN_MILLI_SECONDS * 60;
const DAY_IN_MILLI_SECONDS = HOUR_IN_MILLI_SECONDS * 24;
const MONTH_IN_MILLI_SECONDS = DAY_IN_MILLI_SECONDS * 30;
const YEAR_IN_MILLI_SECONDS = DAY_IN_MILLI_SECONDS * 365;

// Degraus da forma compacta, do maior para o menor. Abaixo de um minuto nao ha
// degrau: e "agora".
const COMPACT_UNITS = [
  ['YEARS', YEAR_IN_MILLI_SECONDS],
  ['MONTHS', MONTH_IN_MILLI_SECONDS],
  ['DAYS', DAY_IN_MILLI_SECONDS],
  ['HOURS', HOUR_IN_MILLI_SECONDS],
  ['MINUTES', MINUTE_IN_MILLI_SECONDS],
];

const compactUnit = elapsed => {
  const degrau = COMPACT_UNITS.find(([, size]) => elapsed >= size);

  return degrau ? [degrau[0], Math.floor(elapsed / degrau[1])] : [null, 0];
};

import {
  dynamicTime,
  dateFormat,
  shortTimestamp,
  getCurrentUserLocale,
} from 'shared/helpers/timeHelper';

export default {
  name: 'TimeAgo',
  props: {
    isAutoRefreshEnabled: {
      type: Boolean,
      default: true,
    },
    lastActivityTimestamp: {
      type: [String, Date, Number],
      default: '',
    },
    createdAtTimestamp: {
      type: [String, Date, Number],
      default: '',
    },
    // Mostra so a ultima atividade, na forma curta. O default preserva o
    // formato de dois tempos que o resto do app ja usa.
    compact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      timer: null,
      // Tique reativo. `$forceUpdate()` re-executa so o watcher de render e
      // deixa as computed com `dirty === false`, entao o horario relativo
      // congelava: uma linha que renderizou "5m" continuava "5m" enquanto
      // estivesse montada, e no modo compacto esse e o UNICO indicador de tempo
      // da conversa. Fazendo as contas dependerem deste campo, o intervalo
      // volta a mover o texto.
      now: Date.now(),
    };
  },
  computed: {
    currentLocale() {
      return getCurrentUserLocale(this);
    },
    lastActivityAtTimeAgo() {
      return dynamicTime(this.lastActivityTimestamp, this.currentLocale);
    },
    createdAtTimeAgo() {
      return dynamicTime(this.createdAtTimestamp, this.currentLocale);
    },
    lastActivityTime() {
      return shortTimestamp(this.lastActivityAtTimeAgo);
    },
    createdAtTime() {
      return shortTimestamp(this.createdAtTimeAgo);
    },
    // Forma compacta calculada do timestamp, nao traduzida de uma frase.
    //
    // Antes ela passava por `shortTimestamp(dynamicTime(ts, 'en'))`, e isso
    // trazia dois defeitos. O primeiro: os degraus grandes do helper sao
    // abreviacoes inglesas ('2mo', '1y'), entao uma lista em portugues exibia
    // ingles -- so a palavra 'now' tinha sido traduzida. O segundo: o helper so
    // reconhece frases no passado; se o relogio do navegador estivesse atras do
    // servidor, o timestamp caia no futuro, o date-fns devolvia "in less than a
    // minute", nada casava e a frase inteira ia parar na celula `shrink-0` da
    // linha, empurrando etiquetas e SLA para fora.
    //
    // Contar os degraus aqui resolve os dois: a unidade sai de uma chave de
    // i18n e o futuro vira "agora", que e o que ele significa na pratica.
    compactTime() {
      const elapsed = Math.max(
        this.now - this.timestampInMilliSeconds(this.lastActivityTimestamp),
        0
      );

      const [key, count] = compactUnit(elapsed);

      return key
        ? this.$tc(`CHAT_LIST.CHAT_TIME_STAMP.${key}`, count, { count })
        : this.$t('CHAT_LIST.CHAT_TIME_STAMP.NOW');
    },
    displayText() {
      return this.compact
        ? this.compactTime
        : `${this.createdAtTime} • ${this.lastActivityTime}`;
    },
    createdAt() {
      const createdTimeDiff = this.now - this.createdAtTimestamp * 1000;
      const isBeforeAMonth = createdTimeDiff > DAY_IN_MILLI_SECONDS * 30;
      return !isBeforeAMonth
        ? `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.CREATED.LATEST')} ${
            this.createdAtTimeAgo
          }`
        : `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.CREATED.OLDEST')} ${dateFormat(
            this.createdAtTimestamp,
            'MMM d, yyyy',
            this.currentLocale
          )}`;
    },
    lastActivity() {
      const lastActivityTimeDiff = this.now - this.lastActivityTimestamp * 1000;
      const isNotActive = lastActivityTimeDiff > DAY_IN_MILLI_SECONDS * 30;
      return !isNotActive
        ? `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.LAST_ACTIVITY.ACTIVE')} ${
            this.lastActivityAtTimeAgo
          }`
        : `${this.$t(
            'CHAT_LIST.CHAT_TIME_STAMP.LAST_ACTIVITY.NOT_ACTIVE'
          )} ${dateFormat(
            this.lastActivityTimestamp,
            'MMM d, yyyy',
            this.currentLocale
          )}`;
    },
    tooltipText() {
      return `${this.createdAt}
              ${this.lastActivity}`;
    },
  },
  mounted() {
    if (this.isAutoRefreshEnabled) {
      this.timer = setInterval(() => {
        this.now = Date.now();
      }, 60000);
    }
  },
  // `beforeDestroy` e nao `beforeUnmount`: este e Vue 2.7, onde `beforeUnmount`
  // nao existe na lista de hooks e e ignorado SEM aviso -- o intervalo de 60s
  // criado no `mounted` nunca era limpo. Com um TimeAgo por linha dentro da
  // lista virtualizada, cada linha reciclada deixava para tras um tique por
  // minuto num componente ja destruido.
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  },
  methods: {
    // Os timestamps chegam em segundos unix, como o resto do componente ja
    // assume ao multiplicar por 1000.
    timestampInMilliSeconds(timestamp) {
      return Number(timestamp) * 1000;
    },
    refreshTimer() {
      if (this.timer) {
        clearInterval(this.timer);
      }
      if (this.isAutoRefreshEnabled) {
        this.timer = setInterval(() => {
          this.now = Date.now();
        }, 60000);
      }
    },
  },
};
</script>

<template>
  <div
    v-tooltip.top="{
      content: tooltipText,
      delay: { show: 1500, hide: 0 },
      hideOnClick: true,
    }"
    class="leading-4 text-xxs text-subtle hover:text-body transition-colors"
    :class="{ 'ml-auto': !compact }"
  >
    <span>{{ displayText }}</span>
  </div>
</template>
